/**
 * PrepScreen – Phase 2 vocabulary-prep gate before a live session.
 *
 * Route: `/prep` accepts these query params:
 *
 *   mode          "free_talk" | "scenario" | "teacher"    (required)
 *   scenario_id   <uuid>                                   (when mode=scenario)
 *   topic         <string>                                 (optional, free-talk)
 *
 * Flow:
 *   1. GET /api/vocab/prep → ~6 focus words, scenario intro if applicable.
 *   2. Learner picks which words to FOCUS (tap to toggle). At least 3 are
 *      pre-selected for them — they can deselect or pick more.
 *   3. On confirm:
 *        - scenario or free_talk → POST /api/session/start with prep_words
 *        - teacher → POST /api/teacher/start with prep_words
 *      then navigate to /session/:id.
 *   4. Users who have already completed 5+ prep sessions see a "Skip prep"
 *      link that starts the session with no prep_words.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CaretLeft,
  CaretRight,
  Play,
  CheckCircle,
  Circle,
  SpeakerHigh,
  Target,
  Sparkle,
  ArrowSquareOut,
  SpinnerGap,
} from '@phosphor-icons/react';
import { http, API, apiErrorMessage, getStoredToken } from '@/lib/api';

// Cap of pre-selected focus words on first render.
const PRESELECT = 3;

export default function PrepScreen() {
  const navigate = useNavigate();
  const [qs] = useSearchParams();
  const mode = qs.get('mode') || 'free_talk';
  const scenarioId = qs.get('scenario_id') || null;
  const topic = qs.get('topic') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]); // [{lemma, translation, ..., focus}]
  const [scenario, setScenario] = useState(null); // {title, intro, communication_goal}
  const [canSkip, setCanSkip] = useState(false);
  const [prepCount, setPrepCount] = useState(0);
  const [starting, setStarting] = useState(false);
  const [speaking, setSpeaking] = useState(null); // lemma currently playing

  // --- Fetch prep words --------------------------------------------------
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await http.post('/vocab/prep', {
          mode: mode === 'teacher' ? 'free_talk' : mode,
          scenario_id: scenarioId,
          topic: topic || null,
          limit: 6,
        });
        // Preselect first 3 as focus.
        const withFocus = (data.items || []).map((it, i) => ({
          ...it,
          focus: i < PRESELECT,
        }));
        setItems(withFocus);
        setScenario(data.scenario || null);
        setCanSkip(!!data.can_skip);
        setPrepCount(data.prep_sessions_count || 0);
      } catch (e) {
        setError(apiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [mode, scenarioId, topic]);

  // --- Tap-to-focus toggle ----------------------------------------------
  function toggleFocus(idx) {
    setItems((curr) =>
      curr.map((it, i) => (i === idx ? { ...it, focus: !it.focus } : it))
    );
  }

  // --- Pronounce a lemma via /api/tts -----------------------------------
  async function speak(it) {
    if (speaking === it.lemma) return;
    setSpeaking(it.lemma);
    try {
      const token = getStoredToken();
      const res = await fetch(`${API}/tts`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text: it.example_target || it.lemma, speed: 0.9 }),
      });
      if (!res.ok) throw new Error('tts failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => setSpeaking(null);
      audio.onerror = () => setSpeaking(null);
      await audio.play();
    } catch {
      setSpeaking(null);
    }
  }

  // --- Start the session (with or without prep) -------------------------
  async function startSession({ skip }) {
    setStarting(true);
    setError('');
    const prep = skip ? [] : items.filter((it) => it.focus);
    try {
      let res;
      if (mode === 'teacher') {
        res = await http.post('/teacher/start', {
          mode: scenarioId ? 'scenario' : 'free_talk',
          scenario_id: scenarioId,
          prep_words: prep,
        });
        navigate(`/session/${res.data.id}?teacher=1`);
      } else if (mode === 'scenario') {
        res = await http.post('/session/start', {
          mode: 'scenario',
          scenario_id: scenarioId,
          prep_words: prep,
        });
        navigate(`/session/${res.data.id}`);
      } else {
        res = await http.post('/session/start', {
          mode: 'free_talk',
          prep_words: prep,
        });
        navigate(`/session/${res.data.id}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && typeof detail === 'object') {
        setError(detail.message || 'Upgrade to continue.');
        const reason = detail.kind || 'free_daily_cap';
        setTimeout(() => navigate(`/pricing?reason=${reason}`), 900);
        return;
      }
      setError(apiErrorMessage(e));
    } finally {
      setStarting(false);
    }
  }

  const focusCount = useMemo(() => items.filter((i) => i.focus).length, [items]);

  // --- UI ---------------------------------------------------------------
  return (
    <div className="lf-shell flex flex-col px-6 py-8" data-testid="prep-page">
      <div className="flex items-center gap-2">
        <Link
          to="/home"
          className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]"
          data-testid="prep-back"
        >
          <CaretLeft size={18} />
        </Link>
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
            Quick warm-up · {prepCount + 1} of 5
          </div>
          <h1 className="text-2xl font-bold">
            {mode === 'teacher' ? 'Teacher session' : mode === 'scenario' ? 'Scenario prep' : 'Prep your words'}
          </h1>
        </div>
      </div>

      {/* Scenario intro card */}
      {scenario ? (
        <motion.div
          className="lf-card mt-5 p-5 bg-gradient-to-br from-[#FFFFFF] to-[#F7EFE9]"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="prep-scenario-intro"
        >
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-[var(--lf-primary)] text-white">
              <Sparkle size={18} weight="fill" />
            </div>
            <div className="flex-1">
              <div className="text-lg font-bold">{scenario.title}</div>
              <p className="mt-1 text-sm text-[var(--lf-text)]">{scenario.intro}</p>
              {scenario.communication_goal ? (
                <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[var(--lf-surface-muted)] px-3 py-1 text-xs font-semibold text-[var(--lf-primary)]">
                  <Target size={13} weight="fill" />
                  {scenario.communication_goal}
                </div>
              ) : null}
            </div>
          </div>
        </motion.div>
      ) : null}

      {/* Instructions */}
      <div className="mt-5 text-sm text-[var(--lf-text-muted)]" data-testid="prep-instructions">
        {mode === 'scenario'
          ? 'Tap a word to make it a focus. The AI will gently encourage you to use your focus words during the session.'
          : 'Here are a few useful words to warm up with. Tap any to focus — the AI will nudge you to use them.'}
      </div>

      {error ? <div className="mt-3 text-sm text-[var(--lf-destructive)]">{error}</div> : null}

      {/* Word list */}
      {loading ? (
        <div className="mt-10 flex items-center gap-2 text-sm text-[var(--lf-text-muted)]">
          <SpinnerGap className="animate-spin" size={18} /> Loading your warm-up…
        </div>
      ) : (
        <div className="mt-4 space-y-2.5" data-testid="prep-word-list">
          <AnimatePresence>
            {items.map((it, idx) => (
              <motion.div
                key={it.lemma + idx}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ delay: idx * 0.04 }}
                className={`lf-card flex items-start gap-3 p-4 transition-all ${
                  it.focus ? 'ring-2 ring-[var(--lf-primary)] bg-gradient-to-br from-[#FFFFFF] to-[#FFF5EE]' : ''
                }`}
                data-testid={`prep-word-${idx}`}
              >
                <button
                  onClick={() => toggleFocus(idx)}
                  aria-label={it.focus ? 'Remove focus' : 'Add focus'}
                  className="mt-0.5 text-[var(--lf-primary)]"
                  data-testid={`prep-focus-toggle-${idx}`}
                >
                  {it.focus ? (
                    <CheckCircle size={24} weight="fill" />
                  ) : (
                    <Circle size={24} weight="regular" className="text-[var(--lf-text-muted)]" />
                  )}
                </button>
                <div className="flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-bold">{it.lemma}</span>
                    <span className="lf-chip text-[10px]">{it.cefr_level}</span>
                  </div>
                  <div className="text-sm text-[var(--lf-text-muted)]">{it.translation}</div>
                  {it.example_target ? (
                    <div className="mt-1.5 text-sm italic text-[var(--lf-text)]">
                      “{it.example_target}”
                    </div>
                  ) : null}
                </div>
                <button
                  onClick={() => speak(it)}
                  disabled={speaking === it.lemma}
                  className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)] text-[var(--lf-primary)] hover:bg-[var(--lf-surface-muted)]"
                  aria-label="Hear pronunciation"
                  data-testid={`prep-speak-${idx}`}
                >
                  {speaking === it.lemma ? (
                    <SpinnerGap size={16} className="animate-spin" />
                  ) : (
                    <SpeakerHigh size={16} weight="fill" />
                  )}
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Sticky start button */}
      <div className="h-24" />
      <div className="fixed inset-x-0 bottom-0 border-t border-[var(--lf-border)] bg-[var(--lf-surface)] px-6 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-screen-sm items-center gap-3">
          {canSkip ? (
            <button
              onClick={() => startSession({ skip: true })}
              disabled={starting || loading}
              className="lf-btn lf-btn-ghost py-3 text-sm"
              data-testid="prep-skip-btn"
            >
              Skip prep
            </button>
          ) : null}
          <button
            onClick={() => startSession({ skip: false })}
            disabled={starting || loading || focusCount === 0}
            className="lf-btn lf-btn-primary flex-1 py-3"
            data-testid="prep-start-btn"
          >
            {starting ? (
              <>
                <SpinnerGap className="animate-spin" size={16} /> Starting…
              </>
            ) : (
              <>
                Start with {focusCount} focus word{focusCount === 1 ? '' : 's'}
                <CaretRight size={16} weight="bold" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
